import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Flex,
  Heading,
  Spinner,
  Text,
  VStack,
  useToast,
} from '@chakra-ui/react';
import baseUrl from '../../../config/baseURL';
import { getCurentOrgDataFromStorage } from '../../../services/organization';

const FacebookLogInSuccessPage: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState('Finalizing your Facebook connection...');
  const [orgId, setOrgId] = useState<string>('');
  const [pageName, setPageName] = useState<string>('');
  const toast = useToast();

  useEffect(() => {
    const run = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      const oauthError = urlParams.get('error');

      const orgResponse = await getCurentOrgDataFromStorage();
      const resolvedOrgId =
        orgResponse?.success && orgResponse.data && typeof orgResponse.data === 'object'
          ? orgResponse.data.id
          : '';

      if (!resolvedOrgId) {
        setStatus('error');
        setMessage('Failed to retrieve organization data.');
        return;
      }

      setOrgId(resolvedOrgId);

      if (oauthError) {
        setStatus('error');
        setMessage('Meta authorization was not completed.');
        return;
      }

      if (!code) {
        setStatus('error');
        setMessage('No authorization code was returned from Meta.');
        return;
      }

      try {
        const response = await fetch(`${baseUrl}/core/facebook/verification/callback`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            org_id: resolvedOrgId,
            code,
          }),
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.error || 'Failed to connect Facebook Page.');
        }

        setStatus('success');
        setPageName(data.page_name || '');
        toast({
          title: 'Facebook Page connected',
          description: 'The inbox is ready to use.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (err: any) {
        setStatus('error');
        setMessage(err.message || 'Failed to complete Facebook setup.');
      }
    };

    run();
  }, [toast]);

  return (
    <Box minH="100vh" bg="#f5f7fb" px={4} py={8}>
      <Flex minH="calc(100vh - 64px)" align="center" justify="center">
        <Box
          w="full"
          maxW="520px"
          bg="white"
          border="1px solid"
          borderColor="blackAlpha.100"
          borderRadius="28px"
          boxShadow="0 24px 60px rgba(15,23,32,0.06)"
          px={8}
          py={10}
        >
          {status === 'loading' ? (
            <VStack spacing={5}>
              <Spinner size="xl" color="#1877F2" thickness="4px" />
              <VStack spacing={2}>
                <Heading fontSize="2xl" letterSpacing="-0.03em" color="gray.900">
                  Connecting Facebook
                </Heading>
                <Text color="gray.600" textAlign="center">
                  {message}
                </Text>
              </VStack>
            </VStack>
          ) : null}

          {status === 'success' ? (
            <VStack spacing={5} align="stretch">
              <VStack spacing={2} align="flex-start">
                <Heading fontSize="2xl" letterSpacing="-0.03em" color="gray.900">
                  Facebook Page connected
                </Heading>
                <Text color="gray.600" lineHeight="1.7">
                  {pageName ? `${pageName} is now connected.` : 'Your page is now connected.'} Messages and comments can now flow into the inbox.
                </Text>
              </VStack>
              <Button
                bg="#1877F2"
                color="white"
                _hover={{ bg: '#1667d9' }}
                borderRadius="16px"
                onClick={() => {
                  window.location.href = `/organization/${orgId}/inbox/facebook/conversations`;
                }}
              >
                Open Facebook Inbox
              </Button>
            </VStack>
          ) : null}

          {status === 'error' ? (
            <VStack spacing={5} align="stretch">
              <Alert status="error" borderRadius="16px">
                <AlertIcon />
                {message}
              </Alert>
              <Button
                variant="outline"
                borderRadius="16px"
                onClick={() => {
                  window.location.href = '/organization/inbox/facebook-messenger';
                }}
              >
                Try again
              </Button>
            </VStack>
          ) : null}
        </Box>
      </Flex>
    </Box>
  );
};

export default FacebookLogInSuccessPage;
